import { httpClient } from '@/shared/api/httpClient';
import { crmContracts } from '@/entities/chat/api/chatContracts';
import {
  Message,
  ChatFile,
  Topic,
  Workspace,
  PagedResponse,
} from '@/entities/chat/model/types';

// ============================================
// WORKSPACE API
// ============================================

export const workspaceApi = {
  getWorkspaces: async (): Promise<Workspace[]> => {
    return httpClient.request(crmContracts.getWorkspaces);
  },

  getWorkspace: async (id: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.getWorkspace(id));
  },

  updateWorkspaceName: async (id: string, name: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.updateWorkspaceName(id), {
      body: { name },
    });
  },

  addMember: async (id: string, memberId: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.addWorkspaceMember(id), {
      body: { memberId },
    });
  },

  removeMember: async (id: string, memberId: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.removeWorkspaceMember(id, memberId));
  },

  promoteMember: async (id: string, memberId: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.promoteWorkspaceMember(id, memberId));
  },

  demoteMember: async (id: string, memberId: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.demoteWorkspaceMember(id, memberId));
  },

  archiveWorkspace: async (id: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.archiveWorkspace(id));
  },

  reactivateWorkspace: async (id: string): Promise<Workspace> => {
    return httpClient.request(crmContracts.reactivateWorkspace(id));
  },

  deleteWorkspace: async (id: string): Promise<void> => {
    await httpClient.request(crmContracts.deleteWorkspace(id));
  },
};

// ============================================
// TOPIC API (Chat Channels)
// ============================================

export const topicApi = {
  getTopic: async (id: string): Promise<Topic> => {
    return httpClient.request(crmContracts.getTopic(id));
  },

  getUserTopics: async (workspaceId?: string): Promise<Topic[]> => {
    const query = workspaceId ? { workspaceId } : {};
    return httpClient.request(crmContracts.getUserTopics, { query });
  },

  /** Move o marcador de leitura do servidor até à última mensagem. */
  markRead: async (topicId: string): Promise<void> => {
    await httpClient.request(crmContracts.markTopicRead(topicId));
  },

  getUserPinnedTopics: async (): Promise<Topic[]> => {
    return httpClient.request(crmContracts.getUserPinnedTopics);
  },

  getAllTopics: async (): Promise<Topic[]> => {
    return httpClient.request(crmContracts.getAllTopics);
  },
};

// ============================================
// MESSAGE API
// ============================================

export const messageApi = {
  getMessages: async (
    topicId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PagedResponse<Message>> => {
    return httpClient.request(crmContracts.getMessages(topicId), {
      query: { page, size },
    });
  },

  getPinnedMessages: async (topicId: string): Promise<Message[]> => {
    return httpClient.request(crmContracts.getPinnedMessages(topicId));
  },

  getMessagesByStatus: async (topicId: string, status: string): Promise<Message[]> => {
    return httpClient.request(crmContracts.getMessagesByStatus(topicId, status));
  },
};

// ============================================
// FILE API
// ============================================

export const fileApi = {
  /**
   * Upload em três passos: o chat assina um formulário, o navegador envia o arquivo direto ao
   * storage e o chat confirma que o objeto chegou. Só depois disso o arquivo vai numa mensagem.
   */
  uploadFile: async (topicId: string, file: File): Promise<ChatFile> => {
    const grant = await httpClient.request(crmContracts.requestFileUpload(topicId), {
      body: { name: file.name, contentType: file.type || 'application/octet-stream', size: file.size },
    });

    const form = new FormData();
    Object.entries(grant.fields).forEach(([name, value]) => form.append(name, value));
    // O storage exige o arquivo como último campo do formulário.
    form.append('file', file);
    // fetch e não o axios da app: o storage não pode receber o token nem os headers da API.
    const upload = await fetch(grant.url, { method: grant.method || 'POST', body: form });
    if (!upload.ok) {
      throw new Error(`Upload ao storage falhou (${upload.status})`);
    }

    return httpClient.request(crmContracts.completeFileUpload(topicId, grant.file.id));
  },

  getTopicFiles: async (
    topicId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PagedResponse<ChatFile>> => {
    return httpClient.request(crmContracts.getTopicFiles(topicId), {
      query: { page, size },
    });
  },

  /** O chat devolve uma URL assinada (5 min); o conteúdo vem direto do storage. */
  getFile: async (topicId: string, fileId: string): Promise<Blob> => {
    const { url } = await httpClient.request(crmContracts.getFileDownload(topicId, fileId));
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download do storage falhou (${response.status})`);
    }
    return response.blob();
  },
};

// ============================================
// BACKWARDS COMPATIBILITY - Legacy Chat API
// ============================================

/**
 * @deprecated Use topicApi instead
 */
const chatApi = {
  getChat: async (topicId: string): Promise<Topic> => {
    return topicApi.getTopic(topicId);
  },

  getUserChats: async (_userId: string): Promise<Topic[]> => {
    return topicApi.getUserTopics();
  },

  getChatMessages: async (
    topicId: string,
    page: number = 0,
    size: number = 50
  ): Promise<PagedResponse<Message>> => {
    return messageApi.getMessages(topicId, page, size);
  },
};

// Export types for backwards compatibility
type BackendChat = Topic;
type BackendMessage = Message;
type BackendPage<T> = PagedResponse<T>;
